'use client';

import { useEffect } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Controller, useForm } from 'react-hook-form';

import { WishlistFormField } from '@/features/games/internal/types/wishlists';
import { createWishlist } from '@/features/games/internal/api/wishlists/create-wishlist';
import { editWishlist } from '@/features/games/internal/api/wishlists/edit-wishlist';
import { useRouter } from '@/i18n/routing';
import { Button } from '@/shared/components/primitives/button';
import { FormSubmit } from '@/shared/components/form/form-submit';
import { Field } from '@/shared/components/form/field';
import { Checkbox } from '@/shared/components/primitives/checkbox';
import { InputField } from '@/shared/components/form/input-field';
import { Form } from '@/shared/components/form/form';
import { resolveErrorMessage } from '@/shared/helpers/resolve-error-message';

export type WishlistCreateFormProps = {
  setRootError?: (message: string | null) => void;
  wishlist?: WishlistFormField;
  onSuccess?: (result: WishlistSuccessResponse) => void;
  onCancel?: () => void;
  buttonAlignment?: 'center' | 'right';
};

export type WishlistSuccessResponse =
  | {
      entityId: number;
      name: string;
      isPublic: boolean;
    }
  | undefined;

export const WishlistForm = ({
  setRootError,
  wishlist,
  onSuccess,
  onCancel,
  buttonAlignment = 'center',
}: WishlistCreateFormProps) => {
  const t = useTranslations('Account.wishlists.create.form');
  const locale = useLocale();
  const cancelLabel = t('cancel');
  const router = useRouter();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<WishlistFormField>({
    defaultValues: { ...{ shared: false }, ...wishlist },
    mode: 'onSubmit',
  });

  const onSubmit = async (formData: WishlistFormField) => {
    try {
      let result;
      if (formData.id) {
        result = await editWishlist(formData, locale);
      } else {
        result = await createWishlist(formData, locale);
      }
      router.refresh();
      onSuccess?.(result);
    } catch (e: unknown) {
      console.error(e);
      setError('root', { message: 'unknown' });
    }
  };

  useEffect(() => {
    if (setRootError && errors.root?.message) {
      setRootError(t(`errors.${errors.root.message}`));
    } else if (setRootError) {
      setRootError(null);
    }
  }, [errors.root?.message, setRootError, t]);

  const nameError = resolveErrorMessage(errors.name, t);

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className='flex w-full max-w-3xl flex-col'>
      <InputField
        isRequired
        inputProps={register('name', {
          required: 'required',
          minLength: { value: 2, message: 'errors.min' },
        })}
        errorMessage={nameError}
        type='text'
        placeholder={t('namePlaceholder')}
        fieldClassName='min-h-20 mb-8'
        label={t('nameLabel')}
      />

      <div className='mt-6 flex items-center gap-2'>
        <Controller
          name='shared'
          control={control}
          render={({ field }) => (
            <>
              <Field className='subscribeToNewsletter'>
                <Checkbox
                  id='subscribe'
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(!!checked)}
                />
              </Field>
              <label htmlFor='subscribe' className='cursor-pointer text-sm'>
                {t('subscribeLabel')}
              </label>
            </>
          )}
        />
      </div>

      <div
        className={
          buttonAlignment === 'right'
            ? 'mt-3 flex justify-end gap-2'
            : 'mt-3 flex justify-center gap-2'
        }
      >
        <FormSubmit
          disabled={isSubmitting}
          aria-disabled={isSubmitting}
          className='min-w-32 self-center'
        >
          {isSubmitting ? t('saving') : t('save')}
        </FormSubmit>
        <Button
          className='min-w-32 self-center'
          type='button'
          variant='secondary'
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
      </div>
    </Form>
  );
};
